/**
 * Withdrawals and what backs them (FR-16 – FR-18, FR-79 – FR-84).
 *
 * PRD §6.5 governs the framing: a withdrawal is *observed* from the custodian's
 * records, not declared here. The question this page answers is whether every
 * dollar that left the account has expenses behind it.
 *
 * That is also what the IRS checks first. Its matching program compares the
 * custodian's Form 1099-SA with Form 8889, so a withdrawal in a year means a
 * Form 8889 for that year, and the totals here are the figures that go on it.
 *
 * In the shoebox strategy this list is expected to be empty for years, which
 * is why the page lives in the menu and not in the main navigation.
 */
import { useState } from 'react';
import { api } from '../api.js';
import { claims } from '../auth.js';
import { useApi, useAction } from '../hooks.js';
import {
  ErrorBox,
  Money,
  Notice,
  PageHead,
  Pill,
  Skeleton,
  Spinner,
  centsToInput,
  formatDate,
  inputToCents,
  toast,
} from '../ui.js';
import type { Alert, Distribution, Reconciliation } from '../types.js';

interface ListResponse {
  tax_year: number;
  distributions: Distribution[];
  coverage: {
    total_distributed_cents: number;
    total_substantiated_cents: number;
    unsubstantiated_cents: number;
    estimated_taxable_cents: number;
  };
  alerts: Alert[];
}

const MIN_REASON = 10;

export function Withdrawals() {
  const currentYear = new Date().getFullYear();
  const [taxYear, setTaxYear] = useState(currentYear);
  const list = useApi<ListResponse>(`/api/distributions?tax_year=${taxYear}`);
  const [open, setOpen] = useState<{ id: string; panel: 'match' | 'dismiss' } | null>(null);
  const [adding, setAdding] = useState(false);
  const isAdmin = (claims()?.['cognito:groups'] ?? []).includes('admin');

  const reload = () => {
    setOpen(null);
    list.reload();
  };

  if (list.error) return <ErrorBox error={list.error} />;

  const data = list.data;
  const withdrawals = data?.distributions ?? [];

  return (
    <>
      <PageHead title="Withdrawals">Money that left the HSA, and the expenses that back it.</PageHead>

      <div className="fields two">
        <div className="field">
          <label htmlFor="year">Tax year</label>
          <select id="year" value={taxYear} onChange={(e) => setTaxYear(Number(e.target.value))}>
            {Array.from({ length: 8 }, (_, i) => currentYear - i).map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!data ? (
        <Skeleton rows={3} />
      ) : withdrawals.length === 0 ? (
        <div className="card stack tight">
          <p className="small">No withdrawals recorded for {taxYear}.</p>
          <p className="small muted">
            That is the plan working: the money stays invested. Importing the account activity every
            three months is how a mistaken card swipe gets caught while the receipt still exists.
          </p>
        </div>
      ) : (
        <>
          <div className="facts">
            <div className="fact">
              <span className="k">Withdrawn</span>
              <span className="v">
                <Money cents={data.coverage.total_distributed_cents} />
              </span>
            </div>
            <div className="fact">
              <span className="k">Matched to expenses</span>
              <span className="v">
                <Money cents={data.coverage.total_substantiated_cents} />
              </span>
            </div>
            <div className="fact">
              <span className="k">Not matched</span>
              <span className={`v${data.coverage.unsubstantiated_cents > 0 ? ' tone-stop' : ''}`}>
                <Money cents={data.coverage.unsubstantiated_cents} />
              </span>
            </div>
          </div>
          <p className="xs muted">
            A withdrawal in {taxYear} means Form 8889 with your {taxYear} return. The amount withdrawn should
            agree with your 1099-SA (line 14a); the matched amount supports line 15. Anything not matched is
            taxable, plus 20% if you are under 65. This is a summary, not a tax filing.
          </p>

          <div className="stack tight">
            {withdrawals.map((d) => (
              <Withdrawal
                key={d.distribution_id}
                d={d}
                open={open?.id === d.distribution_id ? open.panel : null}
                onOpen={(panel) => setOpen(panel ? { id: d.distribution_id, panel } : null)}
                onDone={reload}
              />
            ))}
          </div>

          <Form1099 taxYear={taxYear} isAdmin={isAdmin} />
        </>
      )}

      {adding ? (
        <AddWithdrawal
          onDone={() => {
            setAdding(false);
            list.reload();
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <div className="actions">
          <button onClick={() => setAdding(true)}>Record a withdrawal</button>
        </div>
      )}
    </>
  );
}

function Withdrawal({
  d,
  open,
  onOpen,
  onDone,
}: {
  d: Distribution;
  open: 'match' | 'dismiss' | null;
  onOpen: (panel: 'match' | 'dismiss' | null) => void;
  onDone: () => void;
}) {
  const needsExpenses = d.substantiation_status !== 'excluded_from_matching';
  const matched = d.matched_expense_ids.length > 0;

  const unmatch = useAction(async () => {
    await api.post(`/api/distributions/${d.distribution_id}/unmatch`, {
      reason: 'Unmatched to correct the expenses behind this withdrawal.',
    });
    toast('Unmatched. The expenses are ready to claim again.');
    onDone();
  });

  return (
    <div className="card stack tight">
      <div className="row-wrap" style={{ justifyContent: 'space-between' }}>
        <strong>
          <Money cents={d.amount_cents} /> on {formatDate(d.posted_date, { year: 'always' })}
        </strong>
        <span className="row-wrap">
          {!d.expected ? <Pill tone="stop">Unexpected</Pill> : null}
          {!needsExpenses ? (
            <Pill>No match needed</Pill>
          ) : d.substantiation_status === 'full' ? (
            <Pill tone="ok">Fully matched</Pill>
          ) : d.substantiation_status === 'partial' ? (
            <Pill tone="warn">Partly matched</Pill>
          ) : (
            <Pill tone="stop">Not matched</Pill>
          )}
        </span>
      </div>
      <p className="xs muted">
        {[d.distribution_type, d.method].map((s) => s.replace(/_/g, ' ')).join(' · ')} · {d.description_raw}
      </p>
      {matched ? (
        <p className="small muted">
          <Money cents={d.matched_total_cents} /> matched across {d.matched_expense_ids.length} expense
          {d.matched_expense_ids.length === 1 ? '' : 's'}.
        </p>
      ) : null}
      {d.alert_dismissed_reason ? <p className="small muted">Noted: {d.alert_dismissed_reason}</p> : null}

      {needsExpenses || !d.expected ? (
        <div className="actions">
          {needsExpenses && d.substantiation_status !== 'full' ? (
            <button className={open === 'match' ? undefined : 'primary'} onClick={() => onOpen(open === 'match' ? null : 'match')}>
              {open === 'match' ? 'Close' : 'Match expenses'}
            </button>
          ) : null}
          {matched ? (
            <button onClick={() => void unmatch.run()} disabled={unmatch.pending}>
              Unmatch
            </button>
          ) : null}
          {/* A fee needs no expenses behind it, but an unexpected one still
              deserves a word about what it was. */}
          {!d.expected || (needsExpenses && d.substantiation_status !== 'full' && !d.alert_dismissed_reason) ? (
            <button className="quiet" onClick={() => onOpen(open === 'dismiss' ? null : 'dismiss')}>
              {needsExpenses ? 'Add a note instead' : 'Add a note'}
            </button>
          ) : null}
        </div>
      ) : null}
      {unmatch.error ? <ErrorBox error={unmatch.error} /> : null}

      {open === 'match' ? <Matcher distribution={d} onDone={onDone} /> : null}
      {open === 'dismiss' ? <Dismiss distribution={d} onDone={onDone} /> : null}
    </div>
  );
}

interface Suggestion {
  suggestion: {
    distribution_amount_cents: number;
    candidates: {
      expense_id: string;
      date_of_service: string | null;
      provider_name: string | null;
      amount_cents: number;
      running_total_cents: number;
    }[];
    suggested_expense_ids: string[];
    shortfall_cents: number;
  };
}

/** FR-79 — many expenses to one withdrawal, oldest first, with a running total. */
function Matcher({ distribution, onDone }: { distribution: Distribution; onDone: () => void }) {
  const suggest = useApi<Suggestion>(`/api/distributions/${distribution.distribution_id}/suggest`);
  const [selected, setSelected] = useState<Set<string> | null>(null);

  const commit = useAction(async (ids: string[]) => {
    await api.post(`/api/distributions/${distribution.distribution_id}/match`, { expense_ids: ids });
    toast('Matched');
    onDone();
  });

  if (suggest.loading) return <Spinner what="Finding expenses" />;
  if (suggest.error) return <ErrorBox error={suggest.error} />;
  if (!suggest.data) return null;

  const { candidates, suggested_expense_ids, shortfall_cents } = suggest.data.suggestion;
  // Start from the suggestion; after that the selection is the person's.
  const chosen = selected ?? new Set(suggested_expense_ids);
  const total = candidates.filter((c) => chosen.has(c.expense_id)).reduce((sum, c) => sum + c.amount_cents, 0);
  const remaining = distribution.amount_cents - total;

  return (
    <div className="stack tight">
      {shortfall_cents > 0 ? (
        <Notice kind="warn" title="Not enough filed expenses to cover this">
          You are <Money cents={shortfall_cents} /> short across everything ready to claim. File more
          expenses, or add a note saying why the rest is not claimable.
        </Notice>
      ) : null}

      <div className="list">
        {candidates.map((c) => (
          <label className="item" key={c.expense_id} style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={chosen.has(c.expense_id)}
              onChange={(e) => {
                const next = new Set(chosen);
                if (e.target.checked) next.add(c.expense_id);
                else next.delete(c.expense_id);
                setSelected(next);
              }}
            />
            <span>
              <div className="title">{c.provider_name ?? 'Expense'}</div>
              <div className="sub">{formatDate(c.date_of_service, { year: 'always' })}</div>
            </span>
            <span className="end">
              <Money cents={c.amount_cents} />
            </span>
          </label>
        ))}
      </div>

      <p className="small">
        Selected <Money cents={total} /> of <Money cents={distribution.amount_cents} />.{' '}
        {remaining > 0 ? (
          <span className="tone-warn">
            <Money cents={remaining} /> still to cover.
          </span>
        ) : (
          <span className="tone-ok">Fully covered.</span>
        )}
      </p>

      {commit.error ? <ErrorBox error={commit.error} /> : null}
      <div className="actions">
        <button className="primary" onClick={() => void commit.run([...chosen])} disabled={chosen.size === 0 || commit.pending}>
          {commit.pending ? 'Matching…' : 'Match these'}
        </button>
      </div>
      <p className="xs muted">Matched expenses become Reimbursed and leave the amount ready to claim.</p>
    </div>
  );
}

/** FR-80 — an alert is cleared by matching, or by a written reason. Never silently. */
function Dismiss({ distribution, onDone }: { distribution: Distribution; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const dismiss = useAction(async () => {
    await api.post(`/api/distributions/${distribution.distribution_id}/dismiss`, { reason });
    toast('Note saved');
    onDone();
  });
  return (
    <div className="stack tight">
      <div className="field">
        <label htmlFor={`note-${distribution.distribution_id}`}>What was this withdrawal?</label>
        <textarea
          id={`note-${distribution.distribution_id}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="For example: taken on purpose and reported as taxable on the return."
        />
        <span className="hint">Saved on the record. It clears the alert on Home.</span>
      </div>
      {dismiss.error ? <ErrorBox error={dismiss.error} /> : null}
      <div className="actions">
        <button onClick={() => void dismiss.run()} disabled={dismiss.pending || reason.trim().length < MIN_REASON}>
          Save note
        </button>
      </div>
    </div>
  );
}

/**
 * FR-73 / FR-82 — the 1099-SA figure for the year, against what is recorded.
 * The custodian's number is the one the IRS receives, so a difference here is
 * worth knowing about before filing.
 */
function Form1099({ taxYear, isAdmin }: { taxYear: number; isAdmin: boolean }) {
  const recon = useApi<Reconciliation>(`/api/reconciliation/${taxYear}`);
  const [box1, setBox1] = useState('');
  const save = useAction(async () => {
    await api.put(`/api/reconciliation/${taxYear}/1099sa`, { gross_distributions_cents: inputToCents(box1) });
    toast('1099-SA amount saved');
    setBox1('');
    recon.reload();
  });

  const r = recon.data?.reconciliation;
  return (
    <details className="more">
      <summary>Check against the 1099-SA</summary>
      <div className="stack tight">
        {recon.error ? <ErrorBox error={recon.error} /> : null}
        {r && r.form_1099sa_box1_cents !== null ? (
          <Notice kind={r.delta_cents === 0 ? 'ok' : 'warn'} title={r.delta_cents === 0 ? 'The 1099-SA agrees' : 'The 1099-SA does not agree'}>
            Box 1 is <Money cents={r.form_1099sa_box1_cents} />. {r.delta_explanation}
          </Notice>
        ) : (
          <p className="small muted">
            When the {taxYear} Form 1099-SA arrives, enter box 1 (gross distribution) to check it against
            what is recorded here.
          </p>
        )}
        {isAdmin ? (
          <div className="actions">
            <input
              aria-label="1099-SA box 1 amount"
              inputMode="decimal"
              placeholder={r?.form_1099sa_box1_cents != null ? centsToInput(r.form_1099sa_box1_cents) : '0.00'}
              style={{ width: 140 }}
              value={box1}
              onChange={(e) => setBox1(e.target.value)}
            />
            <button onClick={() => void save.run()} disabled={save.pending || inputToCents(box1) === null}>
              Save
            </button>
          </div>
        ) : null}
        {save.error ? <ErrorBox error={save.error} /> : null}
      </div>
    </details>
  );
}

/** FR-16 — a withdrawal the import has not seen, entered by hand. */
function AddWithdrawal({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const create = useAction(async () => {
    await api.post('/api/distributions', {
      posted_date: date,
      amount_cents: inputToCents(amount),
      description: note.trim() || undefined,
    });
    toast('Withdrawal recorded');
    onDone();
  });
  const cents = inputToCents(amount);
  return (
    <div className="card stack">
      <h3>Record a withdrawal</h3>
      <div className="fields two">
        <div className="field">
          <label htmlFor="w-date">Date it left the account</label>
          <input id="w-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="w-amount">Amount</label>
          <input id="w-amount" inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label htmlFor="w-note">Note (optional)</label>
        <input id="w-note" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      {create.error ? <ErrorBox error={create.error} /> : null}
      <div className="actions end">
        <button onClick={onCancel}>Cancel</button>
        <button className="primary" onClick={() => void create.run()} disabled={create.pending || !date || cents === null || cents <= 0}>
          Record it
        </button>
      </div>
    </div>
  );
}
